import * as THREE from 'three';
import type { Observable, Subscription } from 'rxjs';
import { areSelectionInteractionMetasEqual, EntityInteractionEventType } from '../../manager/selection';
import type {
    EntityHoverEnterEventPayload,
    EntityHoverLeaveEventPayload,
    EntitySelectedEventPayload,
    SelectionClearedEventPayload,
} from '../../manager';
import {
    GlbDatumSlotHighlightRenderer,
    isSameGlbDatumSlotDescriptor,
    type GlbDatumSlotTargetDescriptor,
} from '../datum-slot';
import { GlbSelectionExtension } from '../render/selection-glb-extension';
import type {
    SelectionExtension,
    SelectionExtensionCommandSelectionPayload,
    SelectionExtensionContext,
    SelectionInteractionMeta,
} from '../render/selection-extension';
import {
    expandImportedModelRenderableTargets,
    ImportedAssemblyPartTargetResolver,
    type ImportedModelResolvedTarget,
} from './ImportedAssemblyPartTargetResolver';
import { ImportedBodyLeafTargetResolver } from './ImportedBodyLeafTargetResolver';
import { ImportedModelOverlayRenderer } from './ImportedModelOverlayRenderer';
import {
    ImportedModelSelectionObjectResolver,
} from '../selection/ImportedModelSelectionObjectResolver';
import {
    isImportedTopologySelectionObject,
    resolveImportedTopologyHost,
} from '../selection/ImportedModelTopologyPickResolver';
import { isImportedModelTopologyHost } from '../topology/imported-model-topology-runtime';
import { ImportedModelObjectMetadataService, type ImportedModelObjectMetadata } from '../metadata';
import { resolveComponentInstanceTransformRoot } from '../../assembly/component-instance';
import { isImportedTopologyProxy } from '../selection/imported-topology-proxy-metadata';
import { isFeatureFace, isFeatureLine, isFeaturePoint, isFeatureResultMesh } from '../../mesh/feature';

interface ImportedModelHighlightState {
    /** 当前悬停的实体事件。 */
    entityHovered: EntityHoverEnterEventPayload | null;
    /** 当前选中的实体事件。 */
    entitySelected: EntitySelectedEventPayload | null;
    /** 命令态需要持续高亮的实体事件快照。 */
    commandSelectedEntities: readonly EntitySelectedEventPayload[];
    /** 当前悬停的 datum-slot。 */
    datumSlotHovered: GlbDatumSlotTargetDescriptor | null;
    /** 当前选中的 datum-slot。 */
    datumSlotSelected: GlbDatumSlotTargetDescriptor | null;
}

type ImportedModelEntityVisualMode = 'none' | 'overlay' | 'bbox' | 'bridge';

interface ImportedModelEntityRoute<TPayload> {
    /** 当前事件应采用的视觉路由。 */
    mode: ImportedModelEntityVisualMode;
    /** 透传给兼容 bridge 的原始事件。 */
    payload: TPayload | null;
    /** overlay renderer 需要消费的精确目标列表。 */
    targets: ImportedModelResolvedTarget[];
    /** bbox 模式下需要解析包围盒的目标对象。 */
    boundingBoxTarget: THREE.Object3D | null;
    /** bbox 模式下需要合并计算包围盒的目标对象集合。 */
    boundingBoxTargets: THREE.Object3D[];
}

/**
 * 判断对象是否仍挂在指定矩阵源的子树内。
 *
 * 设计说明：
 * Mate 预览会先移动组件根，再同步命令态高亮。此时 FeatureFace 代理可能有两种状态：
 * 1. 仍挂在组件根下，`object.matrixWorld` 已经包含当前组件位姿；
 * 2. 被 pick host 保留或重挂到组件根外，`object.matrixWorld` 仍是选择瞬间位姿。
 * 两种状态必须用不同矩阵反算 local，否则第一种会把预览位移叠加两遍。
 *
 * @param object 待判断对象。
 * @param expectedAncestor 预期矩阵源对象。
 * @returns 是否为矩阵源自身或其后代。
 */
function isObjectDescendantOf(
    object: THREE.Object3D,
    expectedAncestor: THREE.Object3D,
): boolean {
    let current: THREE.Object3D | null = object;
    while (current) {
        if (current === expectedAncestor) {
            return true;
        }
        current = current.parent;
    }
    return false;
}

/**
 * 判断对象是否不能作为命令态高亮的稳定矩阵源。
 *
 * @param object 待判断对象。
 * @returns 是否为拓扑代理或 pick host。
 */
function isRejectedCommandMatrixSourceObject(object: THREE.Object3D | null | undefined): boolean {
    return isImportedModelTopologyHost(object)
        || isImportedTopologyProxy(object)
        || isFeatureFace(object)
        || isFeatureLine(object)
        || isFeaturePoint(object);
}

/**
 * datum-slot renderer 最小协议。
 * 说明：
 * 1. 把控制器与具体 renderer 解耦，便于测试注入；
 * 2. Task 3 只依赖 hover/selected/dispose 三个能力面。
 */
export interface ImportedModelDatumSlotRenderer {
    /** 设置悬停态 datum-slot。 */
    setHover(descriptor: GlbDatumSlotTargetDescriptor | null): void;
    /** 设置选中态 datum-slot。 */
    setSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void;
    /** 设置测量模式选中态 datum-slot（绿色 overlay）。 */
    setMeasureSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void;
    /** 释放 renderer 资源。 */
    dispose(): void;
}

export interface ImportedModelHighlightControllerOptions {
    /** 导入模型所在场景。 */
    scene: THREE.Scene;
    /** 按运行时对象 ID 查询场景对象或已脱离场景树的实例 source Mesh。 */
    resolveSceneObjectById?: (objectId: string) => THREE.Object3D | null;
    /**
     * 将实例 source Mesh 等特殊对象的有效世界矩阵写入目标矩阵。
     *
     * @returns 已处理该对象时返回 true；普通场景对象返回 false，由高亮层使用 Three.js 父链。
     */
    copyObjectWorldMatrix?: (object: THREE.Object3D, targetMatrix: THREE.Matrix4) => boolean;
    /** 订阅实体交互事件流，由宿主统一提供。 */
    observeEntityInteraction: (eventType: PuppyCAD.EntityInteractionEventType) => Observable<unknown> | null;
    /** 在状态变化后触发宿主重绘。 */
    requestRender?: () => void;
    /** Viewer 注入的帧内视觉调度；非 Viewer 的独立宿主可继续使用同步视觉更新。 */
    visualUpdateScheduler?: ViewerVisualUpdateSchedulerPort;
    /** 兼容旧 SelectionExtension 协议的桥接层。 */
    selectionExtension?: SelectionExtension;
    /** 可注入的 datum-slot renderer。 */
    datumSlotRenderer?: ImportedModelDatumSlotRenderer;
    /** 可注入的 imported-model overlay renderer。 */
    overlayRenderer?: ImportedModelOverlayRenderer;
    /** 可注入的 body leaf 精确目标解析器。 */
    bodyLeafTargetResolver?: ImportedBodyLeafTargetResolver;
    /** 可注入的 assembly/part 目标解析器。 */
    assemblyPartTargetResolver?: ImportedAssemblyPartTargetResolver;
    /** 是否启用普通实体高亮视觉。 */
    entityVisualEnabled?: boolean;
}

/**
 * imported-model 统一高亮控制器。
 *
 * 设计说明：
 * 1. controller 成为 imported-model 普通实体 overlay 与 datum-slot overlay 的唯一编排入口；
 * 2. body / assembly / part 等普通实体高亮统一走 resolver -> renderer，不再在 controller 中直接管理 overlay 资源；
 * 3. `SelectionExtension` 仅保留兼容桥接职责，用于非 imported-model 对象或旧协议调用方。
 */
export class ImportedModelHighlightController {
    private readonly options: ImportedModelHighlightControllerOptions;
    private readonly selectionBridge: SelectionExtension;
    private readonly datumSlotRenderer: ImportedModelDatumSlotRenderer;
    private readonly overlayRenderer: ImportedModelOverlayRenderer | null;
    private readonly bodyLeafTargetResolver: ImportedBodyLeafTargetResolver;
    private readonly assemblyPartTargetResolver: ImportedAssemblyPartTargetResolver;
    private readonly entityVisualEnabled: boolean;
    private readonly subscriptions: Subscription[] = [];
    private readonly state: ImportedModelHighlightState = {
        entityHovered: null,
        entitySelected: null,
        commandSelectedEntities: [],
        datumSlotHovered: null,
        datumSlotSelected: null,
    };
    /** 当前已渲染到视口的实体 hover 事件，用于避免重复重放副作用。 */
    private renderedEntityHovered: EntityHoverEnterEventPayload | null = null;
    /** 当前已渲染到视口的实体 selected 事件，用于避免重复重放副作用。 */
    private renderedEntitySelected: EntitySelectedEventPayload | null = null;
    /** 外部 bridge 保留原有事件去重；自有 overlay 不用该引用替代几何/成员校验。 */
    private renderedEntityHoverSource: EntityHoverEnterEventPayload | null = null;
    /** 外部 selected bridge 最近事件，仅用于避免其它通道重放相同副作用。 */
    private renderedEntitySelectedSource: EntitySelectedEventPayload | null = null;
    /** 上次真正应用视觉时冻结的拓扑版本，不能在比较旧事件时重新读取已变更的对象。 */
    private renderedEntityHoverRevision = -1;
    /** 普通选中通道上次应用的拓扑版本。 */
    private renderedEntitySelectedRevision = -1;
    /** 上次 Hover 使用的几何载体，替换几何后必须更新视觉资源绑定。 */
    private renderedEntityHoverGeometry: THREE.BufferGeometry | null = null;
    /** 上次普通选中使用的几何载体。 */
    private renderedEntitySelectedGeometry: THREE.BufferGeometry | null = null;
    /** 比较空间状态时复用的当前世界矩阵，避免高频生成临时矩阵。 */
    private readonly visualWorldMatrix = new THREE.Matrix4();
    /** Hover 最近一次应用的世界矩阵；位姿改变后包围盒需重新解析。 */
    private readonly renderedEntityHoverWorldMatrix = new THREE.Matrix4();
    /** 普通选中最近一次应用的世界矩阵。 */
    private readonly renderedEntitySelectedWorldMatrix = new THREE.Matrix4();
    /** 当前已渲染到视口的命令态 selected 签名。 */
    private renderedCommandSelectedSignature: string | null = null;
    /** 当前 selected 可视通道是否由命令态高亮接管。 */
    private renderedCommandSelectedActive = false;
    /** 当前实体 hover 使用的视觉通道。 */
    private renderedEntityHoverMode: ImportedModelEntityVisualMode = 'none';
    /** 当前实体 selected 使用的视觉通道。 */
    private renderedEntitySelectedMode: ImportedModelEntityVisualMode = 'none';
    /** 当前已渲染到视口的 datum-slot hover 描述符。 */
    private renderedDatumSlotHovered: GlbDatumSlotTargetDescriptor | null = null;
    /** 当前已渲染到视口的 datum-slot selected 描述符。 */
    private renderedDatumSlotSelected: GlbDatumSlotTargetDescriptor | null = null;
    /** 当前已渲染到视口的 datum-slot 测量选中描述符。 */
    private renderedDatumSlotMeasureSelected: GlbDatumSlotTargetDescriptor | null = null;
    private started = false;
    /** 释放后禁止尚未消费的视觉更新重新创建 GPU 资源。 */
    private disposed = false;
    /** 最终 Hover 为空时需要执行的兼容桥清理，不在 leave 中途改动视觉。 */
    private pendingHoverClear = false;

    /**
     * @param options 运行时依赖。
     */
    public constructor(options: ImportedModelHighlightControllerOptions) {
        this.options = options;
        this.selectionBridge = options.selectionExtension ?? new GlbSelectionExtension();
        this.datumSlotRenderer = options.datumSlotRenderer ?? new GlbDatumSlotHighlightRenderer(options.scene);
        this.bodyLeafTargetResolver = options.bodyLeafTargetResolver ?? new ImportedBodyLeafTargetResolver();
        this.assemblyPartTargetResolver = options.assemblyPartTargetResolver ?? new ImportedAssemblyPartTargetResolver();
        this.entityVisualEnabled = options.entityVisualEnabled ?? true;
        /**
         * `entityVisualEnabled` 只控制普通 hover/selected 是否由本控制器接管。
         * Mate PropertyManager 的命令态多选高亮必须始终拥有独立 overlay：
         * 1. Viewer 会关闭普通 imported 实体视觉，避免和既有 FeatureFace 材质链路重复；
         * 2. 配合选择需要同时高亮两个已吸收实体，普通选择链路只能留下最后一次点击；
         * 3. 因此 overlay renderer 不能跟随普通实体视觉开关一起置空，否则目标已解析但不会真正渲染。
         */
        this.overlayRenderer = options.overlayRenderer ?? new ImportedModelOverlayRenderer(
            options.scene,
            options.copyObjectWorldMatrix,
        );
    }

    /**
     * 启动控制器。
     * @throws 控制器已释放时拒绝重新订阅，防止遗留回调再次持有已失效场景。
     */
    public start(): void {
        if (this.disposed) throw new Error('导入模型高亮控制器已释放，不能重新启动');
        if (this.started) {
            return;
        }

        this.subscribe<EntityHoverEnterEventPayload>(
            EntityInteractionEventType.EntityHoverEnter,
            this.handleHoverEnter,
        );
        this.subscribe<EntityHoverLeaveEventPayload>(
            EntityInteractionEventType.EntityHoverLeave,
            this.handleHoverLeave,
        );
        this.subscribe<EntitySelectedEventPayload>(
            EntityInteractionEventType.EntitySelected,
            this.handleSelected,
        );
        this.subscribe<SelectionClearedEventPayload>(
            EntityInteractionEventType.SelectionCleared,
            this.handleSelectionCleared,
        );

        this.started = true;
    }

    /**
     * 设置 datum-slot hover。
     *
     * @param descriptor datum-slot 描述符；为空时清空 hover。
     */
    public setDatumSlotHover(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        this.state.datumSlotHovered = descriptor;
        this.requestVisualStateSync();
    }

    /**
     * 切换 datum-slot selected 状态。
     *
     * @param descriptor datum-slot 描述符。
     */
    public toggleDatumSlotSelection(descriptor: GlbDatumSlotTargetDescriptor): void {
        this.state.datumSlotSelected = isSameGlbDatumSlotDescriptor(this.state.datumSlotSelected, descriptor)
            ? null
            : descriptor;
        this.requestVisualStateSync();
    }

    /**
     * 直接设置 datum-slot selected 状态（测量模式等批量写入场景）。
     *
     * @param descriptor datum-slot 描述符；为空时清空 selected。
     */
    public setDatumSlotSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        this.state.datumSlotSelected = descriptor;
        this.requestVisualStateSync();
    }

    /**
     * 设置测量模式 datum-slot 选中 overlay（绿色，独立于命令态蓝色 selected）。
     *
     * @param descriptor datum-slot 描述符；为空时清空测量 overlay。
     */
    public setMeasureDatumSlotSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        this.syncDatumSlotMeasureSelected(descriptor);
    }

    /**
     * 清空 datum-slot 状态。
     */
    public clearDatumSlotState(): void {
        this.state.datumSlotHovered = null;
        this.state.datumSlotSelected = null;
        this.requestVisualStateSync();
    }

    /**
     * 设置命令态实体选中高亮。
     *
     * 设计说明：
     * 1. Mate PropertyManager 会连续吸收两个实体，普通全局选择只保留最后一次点击；
     * 2. 命令态高亮只存放事件快照，不写入 Object3D.userData，也不进入 Mate 持久化定义；
     * 3. 传入空数组表示退出命令或清空当前命令选择，恢复普通选中高亮行为。
     *
     * @param payloads 需要持续高亮的实体选择事件快照。
     * @param options 同步选项。
     */
    public setCommandSelectedEntities(
        payloads: readonly EntitySelectedEventPayload[],
        options: { readonly force?: boolean } = {},
    ): void {
        if (options.force && payloads.length > 0) {
            this.renderedCommandSelectedSignature = null;
        }
        if (payloads.length > 0) {
            /**
             * Mate 这类命令态多选拥有 selected 视觉通道的最高优先级。
             * 普通 EntitySelected 只保存最后一次点击，如果这里继续保留它，
             * 后续命令态短暂清空或重放失败时就会回退成单面高亮。
             */
            this.state.entitySelected = null;
        }
        this.state.commandSelectedEntities = payloads.map((payload) => ({
            ...payload,
            selectedObjectIds: [...payload.selectedObjectIds],
            selectedObjects: [...payload.selectedObjects],
        }));
        this.requestVisualStateSync();
    }

    /**
     * 清空命令态实体选中高亮。
     */
    public clearCommandSelectedEntities(): void {
        /**
         * 命令态高亮用于 Mate PropertyManager 这类“列表吸收多个实体”的场景。
         * 清空命令态时如果继续恢复最近一次普通 EntitySelected，画布会残留最后一个实体高亮；
         * 这与 SOLIDWORKS 退出配合命令后恢复默认选择行为的预期不一致。
         */
        this.state.commandSelectedEntities = [];
        this.state.entitySelected = null;
        this.requestVisualStateSync();
    }

    /**
     * 释放控制器资源。
     */
    public dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.options.visualUpdateScheduler?.cancelVisualUpdate(this);
        this.syncDatumSlotMeasureSelected(null);
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
        this.overlayRenderer?.dispose();
        this.selectionBridge.dispose?.();
        this.datumSlotRenderer.dispose();
        this.state.entityHovered = null;
        this.state.entitySelected = null;
        this.state.commandSelectedEntities = [];
        this.state.datumSlotHovered = null;
        this.state.datumSlotSelected = null;
        this.renderedEntityHovered = null;
        this.renderedEntitySelected = null;
        this.renderedEntityHoverSource = null;
        this.renderedEntitySelectedSource = null;
        this.renderedEntityHoverGeometry = null;
        this.renderedEntitySelectedGeometry = null;
        this.renderedDatumSlotHovered = null;
        this.renderedDatumSlotSelected = null;
        this.pendingHoverClear = false;
        this.started = false;
    }

    /**
     * 订阅指定实体交互事件。
     *
     * @param eventType 事件类型。
     * @param handler 事件处理器。
     */
    private subscribe<T>(eventType: PuppyCAD.EntityInteractionEventType, handler: (payload: T) => void): void {
        const observable = this.options.observeEntityInteraction(eventType);
        if (!observable) {
            return;
        }
        this.subscriptions.push(observable.subscribe((payload) => handler(payload as T)));
    }

    private readonly handleHoverEnter = (payload: EntityHoverEnterEventPayload): void => {
        this.state.entityHovered = payload;
        this.pendingHoverClear = false;
        this.requestVisualStateSync();
    };

    private readonly handleHoverLeave = (_payload: EntityHoverLeaveEventPayload): void => {
        this.state.entityHovered = null;
        this.pendingHoverClear = true;
        this.requestVisualStateSync();
    };

    /**
     * 处理实体选中事件。
     *
     * 互斥规则：
     * 1. 同一 kind（selected↔selected）才互斥：实体选中会覆盖 datum-slot selected，反之亦然；
     * 2. 跨 kind（hover↔selected）不互斥：实体选中不影响 datum-slot hover，datum-slot selected 不影响实体 hover；
     * 3. 这样 "点击一个 datum-slot 基准面 + 继续 hover 普通实体" 等组合场景仍能同时存在，同时避免由下游再次抛出的 `EntitySelected` 事件把刚 set 的 datum-slot selected 误清。
     *
     * @param payload 实体选中事件载荷。
     */
    private readonly handleSelected = (payload: EntitySelectedEventPayload): void => {
        const hasObject = Boolean(payload.object && payload.objectId);
        if (this.state.commandSelectedEntities.length > 0) {
            /**
             * 命令态选择由 SelectionUpdated 消费，普通 EntitySelected 只代表全局主选对象。
             * 在 Mate PropertyManager 中二者不能竞争 selected overlay；这里把普通事件转换成
             * “命令态需要重放”的信号，确保点击 UI 或图形区后仍保留两个待配合实体高亮。
             */
            this.state.entitySelected = null;
            if (hasObject) {
                this.state.datumSlotSelected = null;
            }
            this.markCommandSelectedVisualDirty();
            this.requestVisualStateSync();
            return;
        }
        this.state.entitySelected = hasObject ? payload : null;
        if (hasObject) {
            // 只清同 kind 的 datum-slot selected，datum-slot hover 独立保留。
            this.state.datumSlotSelected = null;
        } else if (this.renderedEntitySelectedMode === 'none') {
            this.selectionBridge.onSelectionCleared?.(this.createContext());
        }
        this.requestVisualStateSync();
    };

    /**
     * 处理全局选择清空事件。
     *
     * 说明：
     * 1. `SelectionCleared` 表达"当前场景没有任何东西被选中"——包括视口点击空白、程序化清空；
     * 2. 与零件/装配体保持一致，datum-slot 的 selected 状态也必须一起取消，不在 selected 域里残留 info-only 高亮；
     * 3. hover 状态由鼠标移出自然清理，这里不动；tree-click datum-slot 的流程中先 emit SelectionCleared 再 toggle 设新值，时序上 toggle 会把 datum-slot selected 正确写回，不会被这里误清。
     */
    private readonly handleSelectionCleared = (_payload: SelectionClearedEventPayload): void => {
        this.state.entitySelected = null;
        this.state.datumSlotSelected = null;
        if (this.state.commandSelectedEntities.length > 0) {
            /**
             * 选择清空事件可能来自视口空白点击，也可能来自 UI 控件夺焦后的统一结算。
             * Mate 命令仍处于激活态时不能把这次清空解释为删除 Mate 候选，只需要重绘
             * 命令态 selected overlay，避免底层默认单选视觉残留。
             */
            this.markCommandSelectedVisualDirty();
        }
        if (this.renderedEntitySelectedMode === 'none') {
            this.selectionBridge.onSelectionCleared?.(this.createContext());
        }
        this.requestVisualStateSync();
    };

    /**
     * 同步 imported-model 统一视觉状态。
     *
     * 互斥规则：
     * 1. **同 kind 互斥**：
     *    - hover 域：datum-slot hover 存在时，压掉实体 hover；
     *    - selected 域：datum-slot selected 存在时，压掉实体 selected；
     * 2. **跨 kind 独立**：datum-slot selected 不影响实体 hover；实体 selected 不影响 datum-slot hover；
     * 3. datum-slot hover 与 selected 若指向同一槽位，只渲染 selected 通道，避免双色叠加。
     */
    private readonly syncVisualState = (): void => {
        if (this.disposed) return;
        if (this.pendingHoverClear && this.renderedEntityHoverMode === 'none') {
            this.selectionBridge.onHoverCleared?.(this.createContext());
        }
        this.pendingHoverClear = false;
        const datumHoverDescriptor = isSameGlbDatumSlotDescriptor(
            this.state.datumSlotHovered,
            this.state.datumSlotSelected,
        )
            ? null
            : this.state.datumSlotHovered;

        // Hover 域决策
        if (datumHoverDescriptor) {
            this.syncEntityHovered(null);
            this.syncDatumSlotHovered(datumHoverDescriptor);
        } else {
            this.syncDatumSlotHovered(null);
            this.syncEntityHovered(this.resolveEntityHoveredVisualPayload(this.state.entityHovered));
        }

        // Selected 域决策
        if (this.state.datumSlotSelected) {
            this.syncCommandSelectedEntities([]);
            this.syncEntitySelected(null);
            this.syncDatumSlotSelected(this.state.datumSlotSelected);
        } else if (this.state.commandSelectedEntities.length > 0) {
            this.syncDatumSlotSelected(null);
            if (!this.renderedCommandSelectedSignature) {
                this.syncEntitySelected(null);
            }
            this.syncCommandSelectedEntities(this.state.commandSelectedEntities);
        } else {
            this.syncCommandSelectedEntities([]);
            this.syncDatumSlotSelected(null);
            this.syncEntitySelected(this.resolveEntitySelectedVisualPayload(this.state.entitySelected));
        }

        this.options.requestRender?.();
    };

    /** 将当前视觉状态交给同一帧调度；不延迟上游业务选择事件或命令数据写入。 */
    private requestVisualStateSync(): void {
        if (this.disposed) return;
        if (this.options.visualUpdateScheduler) {
            this.options.visualUpdateScheduler.scheduleVisualUpdate(this, this.syncVisualState);
        } else {
            this.syncVisualState();
        }
    }

    /**
     * 解析普通 selected 通道在当前 Viewer 配置下是否允许渲染。
     *
     * @param payload 当前 selected 事件。
     * @returns 允许渲染的事件；否则返回 null。
     */
    private resolveEntitySelectedVisualPayload(
        payload: EntitySelectedEventPayload | null,
    ): EntitySelectedEventPayload | null {
        if (this.entityVisualEnabled) {
            return payload;
        }

        if (ImportedModelHighlightController.hasExplicitBodyHighlightTargets(payload?.interactionMeta)) {
            return payload;
        }

        if (payload?.object && isImportedTopologySelectionObject(payload.object)) {
            return payload;
        }

        return null;
    }

    /**
     * 解析 hover 通道在当前 Viewer 配置下是否允许渲染。
     *
     * 设计说明：
     * 1. Viewer 会关闭普通 imported 实体 hover，避免场景鼠标经过面/边/点时额外出现 bbox；
     * 2. 设计树 hover 是程序化语义交互，应与导入零件/离线包实体共用 imported-model highlighter；
     * 3. 因此这里只对白名单化的 `feature-tree + imported-model role` 放行。
     *
     * @param payload 当前 hover 事件。
     * @returns 允许渲染的事件；否则返回 null。
     */
    private resolveEntityHoveredVisualPayload(
        payload: EntityHoverEnterEventPayload | null,
    ): EntityHoverEnterEventPayload | null {
        if (this.entityVisualEnabled) {
            return payload;
        }

        if (ImportedModelHighlightController.isFeatureTreeImportedModelEntityHover(payload?.interactionMeta)) {
            return payload;
        }

        return null;
    }

    /**
     * 判断事件是否携带 `.puppy` 特征实体 body 级显式高亮目标。
     *
     * @param interactionMeta 选择交互元信息。
     * @returns 是否具备可直接渲染的 body 级目标。
     */
    private static hasExplicitBodyHighlightTargets(interactionMeta?: SelectionInteractionMeta): boolean {
        return interactionMeta?.glbNodeRole === 'body'
            && Array.isArray(interactionMeta.entityHighlightTargets)
            && interactionMeta.entityHighlightTargets.length > 0;
    }

    /**
     * 判断事件是否为设计树 imported-model 实体语义 hover。
     *
     * 设计说明：
     * Viewer 关闭 `entityVisualEnabled` 是为了屏蔽图形区鼠标经过导入拓扑时的普通实体高亮；
     * 设计树 hover 属于 SolidWorks Dynamic Highlight，必须仍交给 imported-model highlighter 统一处理。
     *
     * @param interactionMeta 交互元信息。
     * @returns 是否应由 imported-model highlighter 接管 hover 视觉。
     */
    private static isFeatureTreeImportedModelEntityHover(interactionMeta?: SelectionInteractionMeta): boolean {
        if (interactionMeta?.source !== 'feature-tree' || interactionMeta.semantic !== 'entity') {
            return false;
        }
        const role = interactionMeta.glbNodeRole;
        return role === 'assembly'
            || role === 'part'
            || role === 'component'
            || role === 'group'
            || role === 'body-folder'
            || role === 'body';
    }

    /**
     * 判断事件是否为设计树 body 实体 hover。
     *
     * @param interactionMeta 交互元信息。
     * @returns 是否为 body 节点 hover。
     */
    private static isFeatureTreeBodyEntityHover(interactionMeta?: SelectionInteractionMeta): boolean {
        return ImportedModelHighlightController.isFeatureTreeImportedModelEntityHover(interactionMeta)
            && interactionMeta?.glbNodeRole === 'body';
    }

    /**
     * 同步实体 hover 视觉。
     *
     * @param payload 目标 hover 事件；为空时清空 hover 视觉。
     */
    private syncEntityHovered(payload: EntityHoverEnterEventPayload | null): void {
        const revision = this.resolveVisualTopologyRevision(payload?.object);
        const geometry = this.resolveVisualGeometry(payload?.object);
        this.readVisualWorldMatrix(payload?.object);
        if (
            payload && this.renderedEntityHovered
            // 自有 overlay/bbox 必须校验完整目标和几何版本；仅保留外部 bridge 叶对象的语义短路。
            && this.renderedEntityHoverMode === 'bridge'
            && (geometry !== null || this.renderedEntityHoverSource === payload)
            && payload.object === this.renderedEntityHovered.object
            && payload.objectId === this.renderedEntityHovered.objectId
            && revision === this.renderedEntityHoverRevision
            && geometry === this.renderedEntityHoverGeometry
            && this.renderedEntityHoverWorldMatrix.equals(this.visualWorldMatrix)
            && areSelectionInteractionMetasEqual(payload.interactionMeta, this.renderedEntityHovered.interactionMeta)
        ) return;
        const route = this.resolveEntityRoute(payload, 'hover');
        if (route.mode === 'none') {
            this.clearRenderedEntityHover();
            return;
        }

        // 同类自有通道由 renderer 增量更新；切换路由仍沿原有清理边界互斥。
        if (route.mode !== this.renderedEntityHoverMode || (route.mode !== 'bbox' && route.mode !== 'overlay')) {
            this.clearRenderedEntityHover();
        }

        if (route.mode === 'overlay' && route.targets.length > 0) {
            this.overlayRenderer?.setHover(route.targets);
        } else if (route.mode === 'bbox' && route.boundingBoxTarget) {
            if (route.boundingBoxTargets.length > 0) {
                this.overlayRenderer?.setHoverBoundingBoxTargets?.(route.boundingBoxTargets);
            } else {
                this.overlayRenderer?.setHoverBoundingBox?.(route.boundingBoxTarget);
            }
        } else if (route.mode === 'bridge' && payload?.object && payload.objectId) {
            this.selectionBridge.onHovered?.({
                meshId: payload.objectId,
                targetObject: payload.object,
                interactionMeta: payload.interactionMeta,
                context: this.createContext(),
            });
        }

        // 冻结本次已应用的语义；外部 bridge 保留原去重，自有 overlay/bbox 仍校验当前完整目标。
        this.renderedEntityHovered = payload ? {
            ...payload,
            interactionMeta: payload.interactionMeta ? structuredClone(payload.interactionMeta) : undefined,
        } : null;
        this.renderedEntityHoverMode = route.mode;
        this.renderedEntityHoverSource = payload;
        this.renderedEntityHoverRevision = revision;
        this.renderedEntityHoverGeometry = geometry;
        this.renderedEntityHoverWorldMatrix.copy(this.visualWorldMatrix);
    }

    /**
     * 同步实体 selected 视觉。
     *
     * @param payload 目标 selected 事件；为空时清空 selected 视觉。
     */
    private syncEntitySelected(payload: EntitySelectedEventPayload | null): void {
        const revision = this.resolveVisualTopologyRevision(payload?.object);
        const geometry = this.resolveVisualGeometry(payload?.object);
        this.readVisualWorldMatrix(payload?.object);
        if (
            payload && this.renderedEntitySelected
            && this.renderedEntitySelectedMode === 'bridge'
            && (geometry !== null || this.renderedEntitySelectedSource === payload)
            && payload.object === this.renderedEntitySelected.object
            && payload.objectId === this.renderedEntitySelected.objectId
            && revision === this.renderedEntitySelectedRevision
            && geometry === this.renderedEntitySelectedGeometry
            && this.renderedEntitySelectedWorldMatrix.equals(this.visualWorldMatrix)
            // 明确矩阵快照要求重新绑定，不把命令预览的矩阵变更当作重复选择丢掉。
            && !payload.interactionMeta?.topologyProxyMatrixSourceWorldMatrix
            && !payload.interactionMeta?.linearComponentPatternSourceObjectIds
            && areSelectionInteractionMetasEqual(payload.interactionMeta, this.renderedEntitySelected.interactionMeta)
        ) return;
        const route = this.resolveEntityRoute(payload, 'selected');
        if (route.mode === 'none') {
            this.clearRenderedEntitySelected();
            return;
        }

        if (route.mode !== 'overlay' || this.renderedEntitySelectedMode !== 'overlay') {
            this.clearRenderedEntitySelected();
        }

        if (route.mode === 'overlay' && route.targets.length > 0) {
            this.overlayRenderer?.setSelected(route.targets);
        } else if (route.mode === 'bridge' && payload?.object && payload.objectId) {
            this.selectionBridge.onSelected?.({
                meshId: payload.objectId,
                targetObject: payload.object,
                interactionMeta: payload.interactionMeta,
                context: this.createContext(),
            });
        }

        this.renderedEntitySelected = payload ? {
            ...payload,
            interactionMeta: payload.interactionMeta ? structuredClone(payload.interactionMeta) : undefined,
        } : null;
        this.renderedEntitySelectedMode = route.mode;
        this.renderedEntitySelectedSource = payload;
        this.renderedEntitySelectedRevision = revision;
        this.renderedEntitySelectedGeometry = geometry;
        this.renderedEntitySelectedWorldMatrix.copy(this.visualWorldMatrix);
    }

    /**
     * 读取拓扑载体的视觉修订；代理自身编号在该载体生命周期内保持不变。
     * @param object 当前交互对象。
     * @returns 当前宿主修订；普通非拓扑对象返回 0。
     */
    private resolveVisualTopologyRevision(object: THREE.Object3D | null | undefined): number {
        if (!object) return 0;
        if (isFeatureResultMesh(object)) return object.getTopologyVisualRevision();
        if (isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)) {
            return object.getOwningResultMesh()?.getTopologyVisualRevision() ?? 0;
        }
        return 0;
    }

    /**
     * 读取当前视觉几何载体，不复制共享 Geometry。
     * @param object 当前交互对象。
     * @returns 网格/线/点持有的几何；逻辑分组返回 null。
     */
    private resolveVisualGeometry(object: THREE.Object3D | null | undefined): THREE.BufferGeometry | null {
        return object instanceof THREE.Mesh || object instanceof THREE.Line || object instanceof THREE.Points
            ? object.geometry : null;
    }

    /**
     * 把当前对象的有效世界矩阵写入比较缓存，包含已脱离场景树的实例 source Mesh。
     * @param object 当前交互对象。
     */
    private readVisualWorldMatrix(object: THREE.Object3D | null | undefined): void {
        if (!object) {
            this.visualWorldMatrix.identity();
            return;
        }
        if (this.options.copyObjectWorldMatrix?.(object, this.visualWorldMatrix)) return;
        object.updateWorldMatrix(true, false);
        this.visualWorldMatrix.copy(object.matrixWorld);
    }

    /**
     * 同步命令态实体 selected 视觉。
     *
     * @param payloads 当前命令吸收的实体事件快照集合。
     */
    private syncCommandSelectedEntities(payloads: readonly EntitySelectedEventPayload[]): void {
        if (payloads.length === 0) {
            this.clearRenderedCommandSelected();
            return;
        }

        const signature = this.createCommandSelectionSignature(payloads);
        if (this.renderedCommandSelectedSignature === signature && this.renderedEntitySelectedMode !== 'overlay') {
            return;
        }

        const targets = payloads.flatMap((payload) => this.resolveEntityRoute(payload, 'selected').targets);
        if (targets.length > 0) {
            if (!this.renderedCommandSelectedActive || this.renderedEntitySelectedMode !== 'overlay') {
                this.clearRenderedCommandSelected();
            }
            /**
             * 命令态配合选择必须优先走 imported-model 自身 overlay。
             * selection extension 负责普通实体选中态，后续场景单击会刷新为单个实体；
             * 配合选择需要保留 PropertyManager 中的整组实体，不能被最后一次点击覆盖。
             */
            this.overlayRenderer?.setCommandSelected(targets);
            this.renderedCommandSelectedSignature = signature;
            this.renderedCommandSelectedActive = true;
            this.renderedEntitySelectedMode = 'overlay';
            this.renderedEntitySelected = null;
            return;
        }

        this.clearRenderedCommandSelected();
        const context = this.createContext();
        const bridgeItems = payloads.flatMap((payload) => {
            if (!payload.object || !payload.objectId) {
                return [];
            }
            return [{
                meshId: payload.objectId,
                targetObject: payload.object,
                interactionMeta: payload.interactionMeta,
                context,
            }];
        });

        if (bridgeItems.length > 0 && this.selectionBridge.onCommandSelected) {
            const commandPayload: SelectionExtensionCommandSelectionPayload = {
                items: bridgeItems,
                context,
            };
            this.selectionBridge.onCommandSelected(commandPayload);
            this.renderedCommandSelectedSignature = signature;
            this.renderedCommandSelectedActive = true;
            this.renderedEntitySelectedMode = 'bridge';
            this.renderedEntitySelected = null;
        }
    }

    /**
     * 标记命令态 selected 视觉需要重绘。
     *
     * 设计说明：
     * 1. 命令态与普通选中拥有独立通道，但普通选择事件仍会触发默认视觉状态同步；
     * 2. 标记签名失效使兼容 bridge 重新接收完整命令态选择；
     * 3. 自有 overlay 每次校验当前目标和几何，未变化资源由 renderer 复用。
     */
    private markCommandSelectedVisualDirty(): void {
        this.renderedCommandSelectedSignature = null;
    }

    /**
     * 清空命令态 selected 视觉。
     */
    private clearRenderedCommandSelected(): void {
        if (!this.renderedCommandSelectedActive) {
            return;
        }

        if (this.renderedEntitySelectedMode === 'overlay' || this.renderedEntitySelectedMode === 'bbox') {
            this.overlayRenderer?.clearCommandSelected();
        } else if (this.renderedEntitySelectedMode === 'bridge') {
            this.selectionBridge.onSelectionCleared?.(this.createContext());
        }

        this.renderedCommandSelectedSignature = null;
        this.renderedCommandSelectedActive = false;
        this.renderedEntitySelected = null;
        this.renderedEntitySelectedMode = 'none';
    }

    /**
     * 创建命令态高亮签名。
     *
     * @param payloads 命令态实体事件快照。
     * @returns 可用于比较是否需要重绘的签名。
     */
    private createCommandSelectionSignature(payloads: readonly EntitySelectedEventPayload[]): string {
        return payloads.map((payload) => {
            const explicitTargets = payload.interactionMeta?.entityHighlightTargets
                ?.map((target) => `${target.objectId}:${target.primitiveIndices?.join(',') ?? '*'}`)
                .join(';') ?? '';
            const topologyProxyMatrix = payload.interactionMeta?.topologyProxyMatrixSourceWorldMatrix
                ?.map((value) => Number.isFinite(value) ? value.toPrecision(12) : 'nan')
                .join(',') ?? '';
            const topologyProxyMatrixSourceObjectId =
                payload.interactionMeta?.topologyProxyMatrixSourceObjectId ?? '';
            return `${payload.objectId ?? 'none'}:${explicitTargets}:${topologyProxyMatrix}:${topologyProxyMatrixSourceObjectId}`;
        }).join('|');
    }

    /**
     * 清空当前 hover 视觉。
     */
    private clearRenderedEntityHover(): void {
        if (this.renderedEntityHoverMode === 'overlay' || this.renderedEntityHoverMode === 'bbox') {
            this.overlayRenderer?.clearHover();
        } else if (this.renderedEntityHoverMode === 'bridge') {
            this.selectionBridge.onHoverCleared?.(this.createContext());
        }

        this.renderedEntityHovered = null;
        this.renderedEntityHoverMode = 'none';
        this.renderedEntityHoverSource = null;
        this.renderedEntityHoverRevision = -1;
        this.renderedEntityHoverGeometry = null;
    }

    /**
     * 清空当前 selected 视觉。
     */
    private clearRenderedEntitySelected(): void {
        if (this.renderedCommandSelectedActive) {
            return;
        }

        if (this.renderedEntitySelectedMode === 'overlay' || this.renderedEntitySelectedMode === 'bbox') {
            this.overlayRenderer?.clearSelected();
        } else if (this.renderedEntitySelectedMode === 'bridge') {
            this.selectionBridge.onSelectionCleared?.(this.createContext());
        }

        this.renderedEntitySelected = null;
        this.renderedEntitySelectedMode = 'none';
        this.renderedEntitySelectedSource = null;
        this.renderedEntitySelectedRevision = -1;
        this.renderedEntitySelectedGeometry = null;
    }

    /**
     * 根据事件决定采用哪种视觉通道。
     * 说明：
     * 1. feature-tree body hover 走 bbox，保持设计树实体节点与导入零件实体节点的一致动态高亮；
     * 2. part / assembly 在 `hover` 阶段回退到 bbox 线框，体现 SolidWorks "悬停大型聚合体时用 bbox" 的观感，同时避免逐 mesh overlay 的视觉噪声；
     * 3. body / part / assembly 在 `selected` 阶段仍使用 overlay，与此前 SelectionExtension 的 cached solid surface 表达保持一致；
     * 4. 非 imported-model 对象兜底走 bridge。
     *
     * @param payload 实体事件。
     * @param kind 事件阶段（hover / selected）。
     * @returns 当前事件的视觉路由。
     */
    private resolveEntityRoute<TPayload extends {
        object?: THREE.Object3D | null;
        objectId?: string | null;
        interactionMeta?: SelectionInteractionMeta;
    }>(payload: TPayload | null, kind: 'hover' | 'selected'): ImportedModelEntityRoute<TPayload> {
        if (!payload?.object || !payload.objectId) {
            return {
                mode: 'none',
                payload,
                targets: [],
                boundingBoxTarget: null,
                boundingBoxTargets: [],
            };
        }

        if (
            kind === 'hover'
            && ImportedModelHighlightController.isFeatureTreeBodyEntityHover(payload.interactionMeta)
        ) {
            const explicitBoundingBoxTargets = this.resolveFeatureTreeBodyBoundingBoxTargets(
                payload.object,
                payload.interactionMeta,
            );
            if (explicitBoundingBoxTargets.length > 0) {
                return {
                    mode: 'bbox',
                    payload,
                    targets: [],
                    boundingBoxTarget: explicitBoundingBoxTargets[0] ?? null,
                    boundingBoxTargets: explicitBoundingBoxTargets,
                };
            }
        }

        if (isImportedTopologySelectionObject(payload.object)) {
            if (kind === 'selected') {
                const topologyProxyTargets = this.resolveImportedTopologyProxyTargets(
                    payload.object,
                    payload.interactionMeta,
                );
                if (topologyProxyTargets.length > 0) {
                    /**
                     * Mate PropertyManager 的命令态多选不能依赖普通 selection 的单选
                     * `entityHighlightTargets`。当 payload 只携带 FeatureFace/Line/Point
                     * 代理时，直接使用代理自身几何绘制 selected overlay，避免 UI 点击或
                     * 普通 EntitySelected 把两面高亮压回最后一个面。
                     */
                    return {
                        mode: 'overlay',
                        payload,
                        targets: topologyProxyTargets,
                        boundingBoxTarget: null,
                        boundingBoxTargets: [],
                    };
                }
            }
            const topologyHost = resolveImportedTopologyHost(payload.object);
            const explicitTargets = topologyHost
                ? this.resolveExplicitInteractionTargets(topologyHost, payload.interactionMeta)
                : [];
            if (kind === 'selected' && explicitTargets.length > 0) {
                /**
                 * 仅当拓扑代理本身没有可绘制几何时，才回退到当次选择快照携带的 primitive 目标。
                 * 这样 feature-tree 等旧入口仍可显示精确 primitive，同时不会污染 Mate 命令的稳定高亮。
                 */
                return {
                    mode: 'overlay',
                    payload,
                    targets: explicitTargets,
                    boundingBoxTarget: null,
                    boundingBoxTargets: [],
                };
            }

            return {
                mode: 'none',
                payload,
                targets: [],
                boundingBoxTarget: null,
                boundingBoxTargets: [],
            };
        }

        const role = payload.interactionMeta?.glbNodeRole;
        const isAggregateRole = role === 'part'
            || role === 'assembly'
            || role === 'component'
            || role === 'group'
            || role === 'body-folder';

        if (kind === 'hover' && isAggregateRole) {
            const normalizedTargetObject = ImportedModelSelectionObjectResolver.normalizeImportedModelHitObject(payload.object);
            if (ImportedModelSelectionObjectResolver.isImportedModelObject(normalizedTargetObject)) {
                return {
                    mode: 'bbox',
                    payload,
                    targets: [],
                    boundingBoxTarget: normalizedTargetObject,
                    boundingBoxTargets: [],
                };
            }
        }

        const targets = this.resolveImportedModelTargets(payload.object, payload.interactionMeta);
        if (targets && targets.length > 0) {
            return {
                mode: 'overlay',
                payload,
                targets,
                boundingBoxTarget: null,
                boundingBoxTargets: [],
            };
        }

        if (this.options.selectionExtension) {
            return {
                mode: 'bridge',
                payload,
                targets: [],
                boundingBoxTarget: null,
                boundingBoxTargets: [],
            };
        }

        return {
            mode: 'none',
            payload,
            targets: [],
            boundingBoxTarget: null,
            boundingBoxTargets: [],
        };
    }

    /**
     * 解析设计树 body hover 应显示 bbox 的真实对象集合。
     *
     * 设计说明：
     * 1. `.puppy` 与普通导入零件的 Body 节点都可能只是设计树语义节点；
     * 2. `entityHighlightTargets` 存在时必须以它为准，确保 bbox 绑定到真实导入 mesh；
     * 3. 旧数据缺少显式目标时，才回退到 imported-model 选择对象归一化结果。
     *
     * @param targetObject 当前 hover 事件锚点。
     * @param interactionMeta 交互元信息。
     * @returns 可用于 bbox 计算的对象集合。
     */
    private resolveFeatureTreeBodyBoundingBoxTargets(
        targetObject: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): THREE.Object3D[] {
        const explicitTargets = this.resolveExplicitInteractionBoundingBoxTargets(targetObject, interactionMeta);
        if (explicitTargets.length > 0) {
            return explicitTargets;
        }

        const topologyHost = resolveImportedTopologyHost(targetObject);
        const normalizedTargetObject = ImportedModelSelectionObjectResolver.normalizeImportedModelHitObject(
            topologyHost ?? targetObject,
        );
        if (!ImportedModelSelectionObjectResolver.isImportedModelObject(normalizedTargetObject)) {
            return [];
        }

        return [normalizedTargetObject];
    }

    /**
     * 解析显式高亮目标对应的 bbox 对象集合。
     *
     * 设计说明：
     * 1. `.puppy` Body 树节点本身可能没有独立 Object3D，真实运行时对象由
     *    `entityHighlightTargets` 指明；
     * 2. 这里优先从当前对象子树找，找不到再从全场景按 uuid 找，避免虚拟 body 与真实 mesh
     *    不在同一子树时静默失败；
     * 3. 该方法只被 feature-tree body hover 调用，不影响场景鼠标 hover 的拓扑逻辑。
     *
     * @param targetObject 当前 hover 事件对象。
     * @param interactionMeta 交互元信息。
     * @returns 可用于 bbox 计算的对象集合。
     */
    private resolveExplicitInteractionBoundingBoxTargets(
        targetObject: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): THREE.Object3D[] {
        if (!Array.isArray(interactionMeta?.entityHighlightTargets)) {
            return [];
        }

        const targetObjectMap = new Map<string, THREE.Object3D>();
        interactionMeta.entityHighlightTargets.forEach((highlightTarget) => {
            const objectId = highlightTarget.objectId?.trim() ?? '';
            if (!objectId || targetObjectMap.has(objectId)) {
                return;
            }

            const matchedObject = targetObject.uuid === objectId
                ? targetObject
                : targetObject.getObjectByProperty('uuid', objectId)
                    ?? this.resolveSceneObjectById(objectId);
            if (matchedObject) {
                targetObjectMap.set(objectId, matchedObject);
            }
        });

        return Array.from(targetObjectMap.values());
    }

    /**
     * 解析 imported-model 普通实体 overlay 目标。
     *
     * @param targetObject 命中的场景对象。
     * @param interactionMeta 交互元信息。
     * @returns 精确高亮目标；若不是 imported-model 对象则返回 `null`。
     */
    private resolveImportedModelTargets(
        targetObject: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): ImportedModelResolvedTarget[] | null {
        const normalizedTargetObject = ImportedModelSelectionObjectResolver.normalizeImportedModelHitObject(targetObject);

        if (!ImportedModelSelectionObjectResolver.isImportedModelObject(normalizedTargetObject)) {
            return null;
        }

        const explicitTargets = this.resolveExplicitInteractionTargets(normalizedTargetObject, interactionMeta);
        if (explicitTargets.length > 0) {
            return explicitTargets;
        }

        if (interactionMeta?.glbNodeRole === 'body') {
            return this.bodyLeafTargetResolver.resolve(normalizedTargetObject);
        }

        return this.assemblyPartTargetResolver.resolve(normalizedTargetObject);
    }

    /**
     * 优先解析事件中显式携带的 entityHighlightTargets。
     * 说明：
     * 1. 该分支用于兼容仍从 feature-tree 事件直接透传精确 primitive 目标的调用方；
     * 2. 若显式目标未命中，再回退到 Task 2 resolver 的语义根策略，避免桥接层丢精度。
     *
     * @param targetObject 当前命中的对象。
     * @param interactionMeta 交互元信息。
     * @returns 可直接渲染的精确目标列表。
     */
    private resolveExplicitInteractionTargets(
        targetObject: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): ImportedModelResolvedTarget[] {
        if (!Array.isArray(interactionMeta?.entityHighlightTargets) || interactionMeta.entityHighlightTargets.length === 0) {
            return [];
        }

        const resolvedTargets: ImportedModelResolvedTarget[] = [];
        interactionMeta.entityHighlightTargets.forEach((highlightTarget) => {
            const matchedObject = targetObject.uuid === highlightTarget.objectId
                ? targetObject
                : targetObject.getObjectByProperty('uuid', highlightTarget.objectId) ?? null;
            if (!matchedObject) {
                return;
            }

            const matrixSourceObject = this.resolveExplicitInteractionMatrixSource(
                matchedObject,
                targetObject,
                interactionMeta,
            );
            const renderableTargets = expandImportedModelRenderableTargets(
                matchedObject,
                this.normalizePrimitiveIndices(highlightTarget.primitiveIndices),
            );
            resolvedTargets.push(
                ...renderableTargets.map((target) => (
                    matrixSourceObject
                        ? this.createMatrixSourceBoundTarget(target, matrixSourceObject, interactionMeta)
                        : target
                )),
            );
        });

        return resolvedTargets;
    }

    /**
     * 校验 selectionProxyObjectId 指向的矩阵源是否仍属于同一装配实例。
     *
     * 设计说明：
     * 1. 同一个零件可在装配体中插入多次，pick host / FeatureFace 可能继承到过期或错误的
     *    selectionProxyObjectId；
     * 2. Mate 命令态高亮只能跟随自身 assemblyNodeId 的组件根，不能把第一个面的 overlay 绑定到
     *    第二个实例根，否则会出现蓝色高亮面被另一实体带走；
     * 3. 缺少任一侧 assemblyNodeId 时保持兼容旧导入数据，由后续父链回退继续兜底。
     *
     * @param metadata 当前高亮目标携带的 imported-model metadata。
     * @param candidate selectionProxyObjectId 指向的候选矩阵源。
     * @returns 是否允许使用该矩阵源。
     */
    private isSelectionProxyMatrixSourceCompatible(
        metadata: ImportedModelObjectMetadata | null | undefined,
        candidate: THREE.Object3D,
    ): boolean {
        const expectedAssemblyNodeId = metadata?.selectionTarget?.assemblyNodeId ?? metadata?.assemblyNodeId ?? '';
        const candidateAssemblyNodeId =
            ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(candidate) ?? '';
        return !expectedAssemblyNodeId || !candidateAssemblyNodeId || expectedAssemblyNodeId === candidateAssemblyNodeId;
    }

    /**
     * 解析显式 entityHighlightTargets 应跟随的组件矩阵源。
     *
     * 设计说明：
     * 1. Mate 命令态会复用 SelectionUpdated 中的 `entityHighlightTargets` 精确绘制面/线/点；
     * 2. 这些目标可能来自按需 pick host，而 pick host 不一定挂在真实组件根下；
     * 3. 因此显式目标也必须读取 imported metadata 中的 `selectionProxyObjectId`，与 topology proxy
     *    路径使用同一套“真实组件根”矩阵来源，避免预览求解后高亮面和实体分离。
     *
     * @param matchedObject entityHighlightTargets 命中的对象。
     * @param targetObject 当前事件携带的查找根。
     * @returns 可随组件 transform 更新的矩阵源；没有稳定来源时返回 `null`。
     */
    private resolveExplicitInteractionMatrixSource(
        matchedObject: THREE.Object3D,
        targetObject: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): THREE.Object3D | null {
        const explicitMatrixSource = this.resolveInteractionMetaMatrixSource(interactionMeta, matchedObject);
        if (explicitMatrixSource) {
            return explicitMatrixSource;
        }

        const metadata = ImportedModelObjectMetadataService.peekMetadataFromSelfOrAncestors(matchedObject)
            ?? ImportedModelObjectMetadataService.peekMetadataFromSelfOrAncestors(targetObject);
        const selectionProxyObjectId = metadata?.selectionProxyObjectId;
        if (!selectionProxyObjectId) {
            return null;
        }

        const candidate = this.resolveSceneObjectById(selectionProxyObjectId);
        if (!candidate || candidate === matchedObject) {
            return null;
        }
        if (!this.isSelectionProxyMatrixSourceCompatible(metadata, candidate)) {
            return null;
        }

        /**
         * metadata 若错误指向 topology host 自身，则继续让 renderer 使用对象父链或世界矩阵兜底。
         * 真实组件根可以等于 targetObject；例如事件本身从组件根发出且显式 targets 指向其子 mesh。
         */
        const candidateTopologyHost = resolveImportedTopologyHost(candidate);
        if (candidateTopologyHost === candidate) {
            return null;
        }

        return this.resolveAcceptedMatrixSourceObject(candidate, matchedObject);
    }

    /**
     * 从命令态交互元信息中解析显式矩阵源。
     *
     * 设计说明：
     * Mate runtime 已经使用组件根解析器决定预览/拖动要写入的真实组件根。
     * 高亮层若再从 pick host metadata 反推一次，旧导入数据会因为缺少 assemblyNodeId
     * 而把 overlay 绑定到错误组件；因此显式来源拥有最高优先级。
     *
     * @param interactionMeta 交互元信息。
     * @param sourceObject 当前高亮来源对象。
     * @returns 可作为 overlay 矩阵源的对象；无效时返回 `null`。
     */
    private resolveInteractionMetaMatrixSource(
        interactionMeta: SelectionInteractionMeta | undefined,
        sourceObject: THREE.Object3D,
    ): THREE.Object3D | null {
        const matrixSourceObjectId = interactionMeta?.topologyProxyMatrixSourceObjectId?.trim();
        if (!matrixSourceObjectId) {
            return null;
        }

        const candidate = this.resolveSceneObjectById(matrixSourceObjectId);
        if (!candidate) {
            return null;
        }

        return this.resolveAcceptedMatrixSourceObject(candidate, sourceObject);
    }

    /**
     * 按场景对象 ID 查找对象。
     *
     * @param objectId 运行时对象 ID。
     * @returns 命中的对象；不存在时返回 `null`。
     */
    private resolveSceneObjectById(objectId: string): THREE.Object3D | null {
        return this.options.resolveSceneObjectById?.(objectId)
            ?? this.options.scene.getObjectByProperty('uuid', objectId)
            ?? this.options.scene.getObjectByName(objectId)
            ?? null;
    }

    /**
     * 复制对象当前有效世界矩阵。
     *
     * 已脱离场景树的实例 source Mesh 必须由 Viewer/Unit 提供有效矩阵；普通对象继续通过
     * Three.js 父链更新。控制器和 overlay renderer 共用同一注入端口，避免创建局部后缀与
     * 渲染前同步分别理解实例化状态。
     *
     * @param object 待读取对象。
     * @param targetMatrix 接收世界矩阵的复用对象。
     */
    private copyEffectiveObjectWorldMatrix(
        object: THREE.Object3D,
        targetMatrix: THREE.Matrix4,
    ): void {
        if (this.options.copyObjectWorldMatrix?.(object, targetMatrix)) {
            return;
        }
        object.updateWorldMatrix(true, false);
        targetMatrix.copy(object.matrixWorld);
    }

    /**
     * 统一校验并收口高亮矩阵源。
     *
     * 设计说明：
     * 该函数复用组件 transform 根解析器，确保高亮跟随的根和 Mate 预览/拖动写入的根一致。
     * 即使传入的是可见 mesh 或旧 metadata 指向的中间节点，也会被收口到组件根；
     * 但拓扑代理和 pick host 永远不能作为矩阵源。
     *
     * @param candidate 候选矩阵源。
     * @param sourceObject 当前高亮来源对象。
     * @returns 可用矩阵源；无效时返回 `null`。
     */
    private resolveAcceptedMatrixSourceObject(
        candidate: THREE.Object3D,
        sourceObject: THREE.Object3D,
    ): THREE.Object3D | null {
        const transformRoot = resolveComponentInstanceTransformRoot(candidate, {
            getSceneObjectById: (objectId) => this.resolveSceneObjectById(objectId),
            isRejectedTransformRoot: isRejectedCommandMatrixSourceObject,
            findTransformRootByAssemblyNodeId: (assemblyNodeId) => (
                this.findCommandMatrixSourceByAssemblyNodeId(assemblyNodeId)
            ),
        }) ?? candidate;
        if (isRejectedCommandMatrixSourceObject(transformRoot)) {
            return null;
        }

        const sourceAssemblyNodeId =
            ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(sourceObject) ?? '';
        const transformAssemblyNodeId =
            ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(transformRoot) ?? '';
        if (sourceAssemblyNodeId && transformAssemblyNodeId && sourceAssemblyNodeId !== transformAssemblyNodeId) {
            return null;
        }

        return transformRoot;
    }

    /**
     * 按 assemblyNodeId 在场景中查找可作为命令态矩阵源的组件根。
     *
     * @param assemblyNodeId 装配实例节点 ID。
     * @returns 可用组件根；找不到时返回 `null`。
     */
    private findCommandMatrixSourceByAssemblyNodeId(assemblyNodeId: string): THREE.Object3D | null {
        let matchedObject: THREE.Object3D | null = null;
        this.options.scene.traverse((object) => {
            if (matchedObject) {
                return;
            }
            if (ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(object) !== assemblyNodeId) {
                return;
            }
            const transformRoot = resolveComponentInstanceTransformRoot(object, {
                getSceneObjectById: (objectId) => this.resolveSceneObjectById(objectId),
                isRejectedTransformRoot: isRejectedCommandMatrixSourceObject,
            });
            if (!transformRoot || isRejectedCommandMatrixSourceObject(transformRoot)) {
                return;
            }
            matchedObject = transformRoot;
        });
        return matchedObject;
    }

    /**
     * 给已解析的 overlay 目标绑定稳定矩阵源。
     *
     * @param target 已展开的可渲染 overlay 目标。
     * @param matrixSourceObject 真实组件根或可稳定跟随 transform 的对象。
     * @param interactionMeta 选择瞬间记录的组件世界矩阵快照。
     * @returns 带矩阵源和局部矩阵的 overlay 目标。
     */
    private createMatrixSourceBoundTarget(
        target: ImportedModelResolvedTarget,
        matrixSourceObject: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): ImportedModelResolvedTarget {
        const worldMatrix = new THREE.Matrix4();
        this.copyEffectiveObjectWorldMatrix(target.object, worldMatrix);

        const currentMatrixSourceWorldMatrix = new THREE.Matrix4();
        this.copyEffectiveObjectWorldMatrix(
            matrixSourceObject,
            currentMatrixSourceWorldMatrix,
        );
        const matrixSourceWorldMatrix = this.resolveTopologyProxyMatrixSourceWorldMatrix(interactionMeta)
            ?? currentMatrixSourceWorldMatrix;
        const localMatrixSourceWorldMatrix = isObjectDescendantOf(target.object, matrixSourceObject)
            ? currentMatrixSourceWorldMatrix
            : matrixSourceWorldMatrix;
        const localMatrixToSource = localMatrixSourceWorldMatrix
            .clone()
            .invert()
            .multiply(worldMatrix);

        return {
            ...target,
            matrixSourceObject,
            localMatrixToSource,
            worldMatrix,
        };
    }

    /**
     * 解析 imported topology proxy 自身可绘制的 overlay 目标。
     *
     * 设计说明：
     * 1. FeatureFace/FeatureLine/FeaturePoint 是导入拓扑的命令态稳定载体；
     * 2. Mate 命令高亮需要与普通单选高亮解耦，因此不能要求每个 payload 都从
     *    SelectionUpdated 继承最后一次点击的 `entityHighlightTargets`；
     * 3. 这里仅在 selected 域使用代理自身几何，不改变 hover 路径，也不批量创建任何代理。
     *
     * @param object imported topology proxy 对象。
     * @returns 可直接交给 overlay renderer 的目标集合。
     */
    private resolveImportedTopologyProxyTargets(
        object: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): ImportedModelResolvedTarget[] {
        /**
         * 命令态高亮常发生在配合预览或拖动刚改完组件 local transform 之后，
         * 此时父组件的 matrixWorld 可能还没等到下一帧 scene.updateMatrixWorld。
         * 这里必须从父链向下刷新，否则 overlay 会复制旧 world matrix，
         * 形成蓝色高亮面和实际实体分离。
         */
        object.updateWorldMatrix(true, false);
        if (object instanceof THREE.Mesh) {
            return [this.createImportedTopologyProxyTarget(object, interactionMeta)];
        }
        if (object instanceof THREE.Line && !(object instanceof THREE.LineSegments)) {
            return [this.createImportedTopologyProxyTarget(object, interactionMeta)];
        }
        if (object instanceof THREE.Sprite) {
            return [this.createImportedTopologyProxyTarget(object, interactionMeta)];
        }
        return [];
    }

    /**
     * 创建 imported 拓扑代理的 overlay 目标。
     *
     * @param object 当前命令态高亮的拓扑代理对象。
     * @returns 可交给 overlay renderer 的目标描述。
     */
    private createImportedTopologyProxyTarget(
        object: THREE.Mesh | THREE.Line | THREE.Sprite,
        interactionMeta?: SelectionInteractionMeta,
    ): ImportedModelResolvedTarget {
        const matrixSourceObject = this.resolveImportedTopologyProxyMatrixSource(object, interactionMeta);
        if (!matrixSourceObject) {
            return {
                object,
                worldMatrix: object.matrixWorld.clone(),
            };
        }

        /**
         * 记录代理相对于真实矩阵源的局部矩阵。
         * 这样即使 FeatureFace/pick host 后续被保留链路重挂，overlay 仍跟随组件根或可见 mesh。
         */
        return this.createMatrixSourceBoundTarget({
            object,
        }, matrixSourceObject, interactionMeta);
    }

    /**
     * 解析命令态拓扑代理携带的矩阵源世界矩阵快照。
     *
     * 设计说明：
     * 1. 该快照来自 Mate 选择瞬间的组件 transform，用于计算代理几何相对组件根的 local matrix；
     * 2. 后续 overlay 仍绑定当前组件根，因此不会在渲染循环里访问旧 transform；
     * 3. 只接受 16 个有限数字，防止错误 payload 把 overlay 矩阵写成 NaN。
     *
     * @param interactionMeta 选择交互元信息。
     * @returns 可用于求逆的 Matrix4；缺失或非法时返回 `null`。
     */
    private resolveTopologyProxyMatrixSourceWorldMatrix(
        interactionMeta?: SelectionInteractionMeta,
    ): THREE.Matrix4 | null {
        const elements = interactionMeta?.topologyProxyMatrixSourceWorldMatrix;
        if (!Array.isArray(elements) || elements.length !== 16) {
            return null;
        }
        if (!elements.every((value) => typeof value === 'number' && Number.isFinite(value))) {
            return null;
        }

        return new THREE.Matrix4().fromArray([...elements]);
    }

    /**
     * 解析 imported 拓扑代理的稳定矩阵来源。
     *
     * @param object 当前命令态拓扑代理对象。
     * @returns 能随组件 transform 更新的矩阵来源；不存在时返回 `null`。
     */
    private resolveImportedTopologyProxyMatrixSource(
        object: THREE.Object3D,
        interactionMeta?: SelectionInteractionMeta,
    ): THREE.Object3D | null {
        const explicitMatrixSource = this.resolveInteractionMetaMatrixSource(interactionMeta, object);
        if (explicitMatrixSource) {
            return explicitMatrixSource;
        }

        const topologyHost = resolveImportedTopologyHost(object);
        const metadataMatrixSource = this.resolveImportedTopologyMetadataMatrixSource(object, topologyHost);
        if (metadataMatrixSource) {
            return metadataMatrixSource;
        }
        if (topologyHost?.parent) {
            return topologyHost.parent;
        }
        return object.parent ?? null;
    }

    /**
     * 从 imported-model metadata 中解析拓扑代理应跟随的真实组件根。
     *
     * 设计说明：
     * 1. Mate 命令态高亮不能依赖普通单选缓存，也不能把 FeatureFace / pick host 当作真实组件；
     * 2. 真实导入装配体会在 metadata 中写入 `selectionProxyObjectId`，它指向可见组件根或语义选择根；
     * 3. 优先使用该稳定根可避免预览 transform 后出现“蓝色高亮面移动了，实体仍在原地”的错位。
     *
     * @param object 当前命令态高亮的拓扑代理对象。
     * @param topologyHost 拓扑代理宿主，通常为按需创建的 FeatureResultMesh。
     * @returns 可随组件 transform 更新的矩阵源；不存在时返回 `null`。
     */
    private resolveImportedTopologyMetadataMatrixSource(
        object: THREE.Object3D,
        topologyHost: THREE.Object3D | null,
    ): THREE.Object3D | null {
        const metadata = ImportedModelObjectMetadataService.peekMetadataFromSelfOrAncestors(object)
            ?? ImportedModelObjectMetadataService.peekMetadataFromSelfOrAncestors(topologyHost);
        const selectionProxyObjectId = metadata?.selectionProxyObjectId;
        if (!selectionProxyObjectId) {
            return null;
        }

        const candidate = this.resolveSceneObjectById(selectionProxyObjectId);
        if (!candidate) {
            return null;
        }
        if (!this.isSelectionProxyMatrixSourceCompatible(metadata, candidate)) {
            return null;
        }

        /**
         * metadata 可能来自旧工程或测试替身，错误地指向 pick host 自身。
         * pick host 在配合预览中不应该作为组件根移动，否则会重新制造高亮/实体分离。
         */
        const candidateTopologyHost = resolveImportedTopologyHost(candidate);
        if (candidateTopologyHost === candidate || candidate === topologyHost || candidate === object) {
            return null;
        }

        return this.resolveAcceptedMatrixSourceObject(candidate, object);
    }

    /**
     * 归一化 primitiveIndices。
     *
     * @param primitiveIndices 原始 primitive 下标。
     * @returns 过滤后的下标集合。
     */
    private normalizePrimitiveIndices(primitiveIndices?: number[]): number[] | undefined {
        if (!Array.isArray(primitiveIndices) || primitiveIndices.length === 0) {
            return undefined;
        }

        const normalized = primitiveIndices.filter(
            (primitiveIndex) => Number.isInteger(primitiveIndex) && primitiveIndex >= 0,
        );

        return normalized.length > 0 ? normalized : undefined;
    }

    /**
     * 同步 datum-slot hover overlay。
     *
     * @param descriptor 目标 hover 描述符；为空时清空 overlay。
     */
    private syncDatumSlotHovered(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        if (isSameGlbDatumSlotDescriptor(this.renderedDatumSlotHovered, descriptor)) {
            return;
        }
        this.datumSlotRenderer.setHover(descriptor);
        this.renderedDatumSlotHovered = descriptor;
    }

    /**
     * 同步 datum-slot selected overlay。
     *
     * @param descriptor 目标 selected 描述符；为空时清空 overlay。
     */
    private syncDatumSlotSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        if (isSameGlbDatumSlotDescriptor(this.renderedDatumSlotSelected, descriptor)) {
            return;
        }
        this.datumSlotRenderer.setSelected(descriptor);
        this.renderedDatumSlotSelected = descriptor;
    }

    /**
     * 同步 datum-slot 测量选中 overlay。
     *
     * @param descriptor 目标测量选中描述符；为空时清空 overlay。
     */
    private syncDatumSlotMeasureSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        if (isSameGlbDatumSlotDescriptor(this.renderedDatumSlotMeasureSelected, descriptor)) {
            return;
        }
        this.datumSlotRenderer.setMeasureSelected(descriptor);
        this.renderedDatumSlotMeasureSelected = descriptor;
        this.options.requestRender?.();
    }

    /**
     * 构建实体高亮扩展上下文。
     *
     * @returns 扩展上下文。
     */
    private createContext(): SelectionExtensionContext {
        return {
            scene: this.options.scene,
            meshes: new Map<string, THREE.Mesh>(),
            selectionMode: 'object',
            highlightEnabled: true,
            isObjectActuallyVisible: (object: THREE.Object3D | null): boolean => {
                let current = object;
                while (current) {
                    if (!current.visible) {
                        return false;
                    }
                    current = current.parent;
                }
                return true;
            },
            isRaycastExcludedObject: (object: THREE.Object3D): boolean => {
                return Boolean(
                    object.userData?.skipSelection
                    || object.userData?.isSelectionHighlight
                    || object.userData?.isImportedModelOverlay
                    || object.userData?.isGlbDatumSlotOverlay,
                );
            },
        };
    }
}
